import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import qs from "qs";
import { ElMessage } from "element-plus";
import router from "@/router";
import { useLoginDialogStore } from "@/store/loginDialog";
import { buildRequestSignHeaders, canonicalizeBody, extractSignPath, shouldSkipSign } from "@/service/signature";
import { openPowerInsufficientDialog } from "@/components/PowerInsufficientDialog";
import {
  isPowerInsufficientMessage,
  markPowerInsufficientHandled,
  showPowerInsufficientRechargeDialog,
} from "@/hooks/usePowerInsufficientDialog";
import { markHttpErrorToastShown } from "@/utils/httpError";
import { localizeViralErrorMessage } from "@/i18n/localizeViralError";
import { dict } from "@/i18n/strings";
import { useLocaleStore } from "@/store/locale";

/** 后端按进程语言返回的原文，展示前改成当前界面语言 */
const DM_SELF_API_MESSAGES = new Set(["you cannot message yourself", "不能给自己发私信", "不能发送私信给自己"]);

/** 是否为「不能给自己发私信」的接口原文（中文或英文） */
export function isDmSelfApiMessage(msg: string): boolean {
  const normalized = msg
    .trim()
    .replace(/[.。!！]+$/u, "")
    .toLowerCase();
  return DM_SELF_API_MESSAGES.has(normalized);
}

/**
 * 把已知接口原文换成当前界面语言。
 * 先匹配爆款库固定报错，再匹配「不能给自己发私信」；其余保持接口原文。
 */
function localizeApiMessage(msg: string): string {
  const viral = localizeViralErrorMessage(msg);
  if (viral) {
    return viral;
  }
  if (!isDmSelfApiMessage(msg)) {
    return msg;
  }
  return dict.dmCannotMessageSelf[useLocaleStore().locale];
}

/** 避免并发 401 多次跳转登录页 */
let isRedirectingToLogin = false;

/** 会话失效：清凭证、标记提示、跳转登录 */
function handleSessionExpired(): void {
  if (isRedirectingToLogin) {
    return;
  }
  isRedirectingToLogin = true;
  sessionStorage.setItem("session_expired", "1");
  common.signOut();
  useLoginDialogStore().open({
    redirectPath: router.currentRoute.value.fullPath,
    sessionExpired: true,
  });
  isRedirectingToLogin = false;
}

/** 从拦截器返回值中安全提取业务 data */
function unwrapResponse<T>(res: unknown): T {
  if (res == null) {
    throw new Error("响应为空");
  }
  const payload = res as { data?: T };
  return (payload.data != null ? payload.data : res) as T;
}

function omitNullishParams(params: Record<string, unknown> | undefined | null): Record<string, unknown> | undefined {
  if (!params) {
    return undefined;
  }
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * 当前端请求开启了 credentials 模式后，服务在配置跨域时 Access-Control-Allow-Origin 不能配置为“*”号，必须明确指定需要跨域请求的IP或域名，否则Access-Control-Allow-Origin配置无效。
当开启credentials后，请求时会携带凭证信息，如：cookie、客户端ssl信息等。credentials的值有3个，分别为include(始终发送凭证)、same-origin(仅在同源时发送凭证)、omit(始终不发送凭证)。
 */

// 创建axios实例
const service = axios.create({
  timeout: 300000, // 默认请求超时5分钟，长耗时接口（如 AI 生成）通过 config 单独设置
  withCredentials: false, //! 当前端请求开启了credentials模式后，服务端在配置跨域时 Access-Control-Allow-Origin不能配置为“*”号，必须明确指定需要跨域请求的IP或域名，否则Access-Control-Allow-Origin配置无效。当开启credentials后，请求时会携带凭证信息，如：cookie、客户端ssl信息等。credentials的值有3个，分别为include(始终发送凭证)、same-origin(仅在同源时发送凭证)、omit(始终不发送凭证)。

  // URL参数提交前的序列化配置，序列化的格式有多种，例如：'indices'、'brackets'、'repeat'、'comma'，请根据需要选择
  paramsSerializer: {
    serialize: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
  },
});

// 请求拦截
service.interceptors.request.use(
  async (config) => {
    const token = common.getToken();
    config.headers.Authorization = token;

    // qs 会把 null 序列化为 key=，但签名 canonicalize 会跳过 null，需先统一剔除
    const params = omitNullishParams(config.params as Record<string, unknown> | undefined);
    config.params = params;

    const signKey = common.getSignKey();
    const method = (config.method || "GET").toUpperCase();
    const path = extractSignPath(config.url || "");
    if (shouldSkipSign(path, token, signKey)) {
      return config;
    }

    if (["POST", "PUT", "PATCH"].includes(method) && config.data && typeof config.data === "object" && !(config.data instanceof FormData)) {
      const bodyStr = canonicalizeBody(method, config.data);
      config.data = bodyStr;
      config.headers["Content-Type"] = "application/json";
    }

    const signHeaders = await buildRequestSignHeaders({
      method,
      url: config.url || "",
      params,
      data: config.data,
      token: token as string,
      signKey: signKey as string,
    });
    Object.assign(config.headers, signHeaders);
    return config;
  },
  (error: AxiosError) => {
    Promise.reject(error);
  },
);

// 响应拦截
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, headers } = response;
    if (response.config.responseType === "blob") {
      return response;
    }
    if (data.code === 401) {
      handleSessionExpired();
      return Promise.reject(data);
    }

    // 如果请求头中有"Full-Response"标志,表明该请求的发起者希望获取该请求的完整响应数据,因此直接返回
    const requestHeaders = response.config.headers["Full-Response"];
    if (requestHeaders) return data;

    // 全局错误处理
    if (data.code !== 200) {
      if (isPowerInsufficientMessage(data.msg)) {
        const requestConfig = response.config as { powerInsufficientRechargeDialog?: boolean };
        if (requestConfig?.powerInsufficientRechargeDialog) {
          void openPowerInsufficientDialog();
        } else {
          void showPowerInsufficientRechargeDialog();
        }
        return Promise.reject(markPowerInsufficientHandled(data));
      }
      const msg = typeof data.msg === "string" ? data.msg.trim() : "";
      if (!(response.config as any)?.silent && msg) {
        messageError(localizeApiMessage(msg));
        return Promise.reject(markHttpErrorToastShown(data));
      }
      return Promise.reject(data);
    }

    // 兼容没有code的接口返回
    if (data.code === undefined) {
      return {
        code: 200,
        data: data,
        message: "",
        headers,
      };
    }
    return data;
  },
  (error: any) => {
    const { response } = error;
    if (!response) {
      if (!window.navigator.onLine) {
        if (!(error.config as any)?.silent) {
          ElMessage.error("网络连接失败");
          return Promise.reject(markHttpErrorToastShown(error));
        }
      } else if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
        // axios 超时（ECONNABORTED）或通用超时
        if (!(error.config as any)?.silent) {
          ElMessage.error("请求超时，请稍后重试");
          return Promise.reject(markHttpErrorToastShown(error));
        }
      } else if (!(error.config as any)?.silent) {
        ElMessage.error("网络异常，请检查网络连接");
        return Promise.reject(markHttpErrorToastShown(error));
      }
      return Promise.reject(error);
    }
    if (response.status === 401) {
      if (!(error.config as { silent?: boolean })?.silent) {
        handleSessionExpired();
      }
      return Promise.reject(error);
    }
    if (response.status === 403) {
      const msg = response.data?.msg || response.data?.message || "无访问权限";
      if (!(error.config as { silent?: boolean })?.silent) {
        messageError(localizeApiMessage(msg));
        return Promise.reject(markHttpErrorToastShown(error));
      }
      return Promise.reject(error);
    }
    if (response.status === 429) {
      const retryAfter = response.headers["retry-after"];
      const msg = retryAfter ? `操作过于频繁，请 ${retryAfter} 秒后再试` : "操作过于频繁，请稍后再试";
      if (!(error.config as { silent?: boolean })?.silent) {
        messageError(msg);
        return Promise.reject(markHttpErrorToastShown(error));
      }
      return Promise.reject(error);
    }
    if (!(error.config as any)?.silent) {
      ElMessage.error("服务器开小差了，正在尝试重新连接。");
      return Promise.reject(markHttpErrorToastShown(error));
    }
    return Promise.reject(error);
  },
);

enum ServerType {
  /** 基础服务 */
  Base,

  /** 主业务服务 */
  Main,

  /** 不使用基地址，外部调用者自行拼接基地址 */
  Null,
}
export { ServerType };

// 拼接基地址
const getUrl = (server: ServerType, api: string): string => {
  let url = "";
  switch (server) {
    case ServerType.Base:
      url = common.getEnvVar("VITE_APP_BASE_URL") + api;
      break;
    case ServerType.Main:
      url = common.getEnvVar("VITE_APP_BUSINESS_URL") + api;
      break;
    default:
      url = api;
  }

  if (common.getEnvVar("VITE_APP_API_TYPE") === "json") {
    // 接口为JSON时，则为url拼上.json后缀，并加时间戳，以阻止缓存
    url = url + ".json?t=" + Date.now();
  } else if (common.getEnvVar("VITE_APP_API_TYPE") === "current") {
    // 接口为Current时，则从浏览器地址栏中取接口基地址
    url = window.location.protocol + "//" + window.location.hostname + ":" + url;
  }
  return url;
};

/**
 * 请求参数接口
 */
interface RequestParams {
  /**
   * 接口地址
   */
  url: string;

  /**
   * URL参数
   */
  params?: object;

  /**
   * Body参数
   */
  data?: object;

  /**
   * 更多请求配置
   */
  config?: AxiosRequestConfig;
}

/**
 * HTTP请求（如果要在请求中捕获异常，请实现catch）
 * @param server
 * @param param
 * @returns
 */
const http = <T>(server: ServerType, { method, url, params, data, config }: RequestParams & { method: string }) => {
  return service.request({ url: getUrl(server, url), method, params: { ...params }, data, ...config }).then((res) => {
    return unwrapResponse<T>(res);
  });
};
export { http };

/**
 * Get请求（如果要在请求中捕获异常，请实现catch）
 * @param server
 * @param param
 * @returns
 */
const httpGet = <T>(server: ServerType, { url, params, config }: RequestParams) => {
  return service
    .get(getUrl(server, url), {
      params,
      ...config,
    })
    .then((res) => {
      return unwrapResponse<T>(res);
    });
};
export { httpGet };

/**
 * Post请求
 * @param server
 * @param param
 * @returns
 */
const httpPost = <T>(server: ServerType, { url, params, data, config }: RequestParams) => {
  // 如果后端接口为JSON服务，则所有POST请求全部转为GET请求
  if (common.getEnvVar("VITE_APP_API_TYPE") === "json") {
    return service.get(getUrl(server, url), { params: { ...params }, ...config }).then((res) => {
      return unwrapResponse<T>(res);
    });
  } else {
    return service.post(getUrl(server, url), data, { params: params, ...config }).then((res) => {
      return unwrapResponse<T>(res);
    });
  }
};
export { httpPost };

/**
 * Put请求
 * @param server
 * @param param
 * @returns
 */
const httpPut = (server: ServerType, { url, params, data, config }: RequestParams) => {
  return service.put(getUrl(server, url), data, { params: params, ...config }).then((res) => {
    return unwrapResponse(res);
  });
};
export { httpPut };

/**
 * Delete请求
 * @param server
 * @param param1
 * @returns
 */
const httpDelete = (server: ServerType, { url, params, data, config }: RequestParams) => {
  return service.delete(getUrl(server, url), { params: params, data: data, ...config }).then((res) => {
    return unwrapResponse(res);
  });
};
export { httpDelete };

/**
 * 文件下载、导出请求
 */
interface httpDownloadFileType {
  /** 请求方式 */
  method: string;
  /** 文件名称 */
  fileName?: string | null;
}
const buildDownloadUrl = (server: ServerType, { url, params }: Pick<RequestParams, "url" | "params">): string => {
  const downloadUrl = getUrl(server, url);
  const cleanedParams = omitNullishParams(params as Record<string, unknown> | undefined);
  if (!cleanedParams || Object.keys(cleanedParams).length === 0) {
    return downloadUrl;
  }

  const query = qs.stringify(cleanedParams, { arrayFormat: "repeat" });
  if (!query) {
    return downloadUrl;
  }

  return downloadUrl + (downloadUrl.includes("?") ? "&" : "?") + query;
};
export { buildDownloadUrl };

const httpDownloadFile = (server: ServerType, { method, url, params, data, fileName, config }: RequestParams & httpDownloadFileType) => {
  return service.request({ url: getUrl(server, url), method, params: { ...params }, data, responseType: "blob", ...config }).then((res) => {
    const link = document.createElement("a");
    if ("download" in link) {
      // 0.1> 获取文件名称
      if (fileName) {
        link.download = fileName;
      } else if (res.headers["content-disposition"]) {
        link.download = decodeURI(res.headers["content-disposition"].replace("attachment; filename=", ""));
      }
      if (link.download === "" || !link.download) {
        const message =
          '无法获取到文件名称，请在服务端接口响应头中添加 "Access-Control-Expose-Headers":"Content-Disposition"，或在前端写死文件名称!';
        messageError(message);
        return res;
      }

      // 0.2> 构建a标签
      const blob = new Blob([res.data], { type: res.headers["content-type"] }); // 将二进制流转为Blob
      link.href = URL.createObjectURL(blob);
      link.style.display = "none";

      // 0.3> 挂载a标签
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 0.4> 释放blob URL地址
      URL.revokeObjectURL(link.href);
    } else {
      messageError("下载时创建link元素失败");
    }
    return res;
  });
};
export { httpDownloadFile };
